import { useCallback, useState } from "react";
import { SubmitHandler } from "react-hook-form";
import { useHistory, useParams } from "react-router-dom";
import { useEditorMedia } from "../../../../../../../../components/global_components/form_components/controlled-rich-text-editor/hooks/useEditorMedia";
import { richTextEditorDocumentJSONStringToMarkdown } from "../../../../../../../../components/global_components/form_components/controlled-rich-text-editor/util/controlledRichTextEditorMarkdown";
import useSite from "../../../../../../../permits/components/inspections/InspectionPanel/useSite";
import { useNotifyNotificationContentCreate } from "../../../../../../hooks/useNotifyNotificationContentCreate";
import {
  ScheduleType,
  useNotifyNotificationContentScheduleMassNotificationAndArchive,
} from "../../../../../../hooks/useNotifyNotificationContentScheduleMassNotificationAndArchive";
import { useNotifyNotificationContentUpdate } from "../../../../../../hooks/useNotifyNotificationContentUpdate";
import { NotifyNotificationsFormData } from "../../../components/NotifyNotificationsForm";
import { PostNotifyNotificationContentResponse } from "../../../../../../../../queries/notify";

export const NotifyNotificationsActionMode = {
  Create: "create",
  Edit: "edit",
} as const;
export type NotifyNotificationsActionMode =
  (typeof NotifyNotificationsActionMode)[keyof typeof NotifyNotificationsActionMode];

export function useNotifyNotificationsActions({
  mode,
  setStep,
  notifyContentId,
}: {
  setStep: (step: "edit" | "review") => void;
  mode: NotifyNotificationsActionMode;
  notifyContentId?: number;
}) {
  const site = useSite();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const parsedId = notifyContentId ?? parseInt(id);

  const [isSaveDraftLoading, setIsSaveDraftLoading] = useState(false);
  const [isSendLoading, setIsSendLoading] = useState(false);
  const [isScheduleLoading, setIsScheduleLoading] = useState(false);

  const { addMediaFile, commitMediaFiles } = useEditorMedia();
  const { createNotificationContent } = useNotifyNotificationContentCreate();
  const { updateNotificationContent } = useNotifyNotificationContentUpdate();
  const { scheduleMassNotificationAndArchive } = useNotifyNotificationContentScheduleMassNotificationAndArchive();

  const convertContentToFormData = useCallback(
    (notifyContent: PostNotifyNotificationContentResponse): NotifyNotificationsFormData => {
      return {
        fromDepartment: notifyContent.departmentSentFrom
          ? { label: notifyContent.departmentSentFrom.name, value: notifyContent.departmentSentFrom.departmentId }
          : null,
        headline: notifyContent.headline,
        body: notifyContent.bodyJSON,
        tagline: notifyContent.tagline ?? "",
        channels: {
          active: notifyContent.channels,
          additionalData: {
            voiceForceSendAll: notifyContent.voiceForceSendAll ?? false,
          },
        },
        subscriptionGroups: notifyContent.subscriptionGroups.map((group) => ({
          label: group.name,
          value: group.id,
        })),
        sendAt: notifyContent.sendAt ? new Date(notifyContent.sendAt) : null,
        archiveAt: notifyContent.archiveAt ? new Date(notifyContent.archiveAt) : null,
        voiceTranscription: notifyContent.voiceBody,
        classificationId: notifyContent.classification
          ? {
              label: notifyContent.classification.classificationText,
              value: notifyContent.classification.classificationId,
            }
          : null,
        geoTargetFeatures: notifyContent.geoTargeting?.featureIds.length
          ? {
              featureIds: notifyContent.geoTargeting?.featureIds ?? [],
              restrictBoundary: notifyContent.geoTargeting?.restrictToGeoTargetBoundary ?? false,
            }
          : null,
      };
    },
    [],
  );

  const saveContent: (data: NotifyNotificationsFormData) => Promise<PostNotifyNotificationContentResponse | null> =
    useCallback(
      async (data) => {
        const updateBody = {
          body: richTextEditorDocumentJSONStringToMarkdown(data.body),
          bodyJson: data.body,
          headline: data.headline,
          tagline: data.tagline ?? null,
          departmentSentFromId: data.fromDepartment?.value ?? null,
          channels: data.channels.active,
          voiceBody: data.voiceTranscription ?? null,
          voiceAudioResourceId: null,
          voiceForceSendAll: data.channels.additionalData.voiceForceSendAll,
          subscriptionGroupIds: data.subscriptionGroups?.map((subscriptionGroup) => subscriptionGroup.value) ?? [],
          sendAt: data.sendAt?.toISOString() ?? null,
          archiveAt: data.archiveAt?.toISOString() ?? null,
          classificationId: data.classificationId?.value ?? null,
          geoTargeting: !!data.geoTargetFeatures?.featureIds.length
            ? {
                featureIds: data.geoTargetFeatures.featureIds ?? [],
                restrictToGeoTargetBoundary: data.geoTargetFeatures.restrictBoundary ?? false,
              }
            : null,
        };

        const content =
          mode === NotifyNotificationsActionMode.Create
            ? await createNotificationContent(updateBody)
            : await updateNotificationContent(parsedId, updateBody);
        if (!content) return null;
        await commitMediaFiles();
        return content;
      },
      [parsedId, updateNotificationContent, commitMediaFiles, mode],
    );

  const onSaveDraft: SubmitHandler<NotifyNotificationsFormData> = useCallback(
    async (data) => {
      setIsSaveDraftLoading(true);
      const content = await saveContent(data);
      setIsSaveDraftLoading(false);
      if (!content) return;
      history.push(`/${site}/notify/notifications?status=drafts`);
    },
    [history, site, saveContent],
  );

  const onContinue = useCallback(async () => {
    setStep("review");
  }, [setStep]);

  const onSend: SubmitHandler<NotifyNotificationsFormData> = useCallback(
    async (data) => {
      setIsSendLoading(true);
      const content = await saveContent(data);
      if (!content) {
        setIsSendLoading(false);
        return;
      }
      const contentIsPublished = content.status === "Active" || content.status === "Archived";
      await scheduleMassNotificationAndArchive({
        contentId: content.id,
        ...(contentIsPublished
          ? {}
          : {
              sendAt: {
                type: ScheduleType.Immediate,
              },
            }),
        archiveAt: data.archiveAt
          ? {
              type: ScheduleType.Schedule,
              date: new Date(data.archiveAt),
            }
          : {
              type: ScheduleType.Unschedule,
            },
      });
      setIsSendLoading(false);
      history.push(`/${site}/notify/notifications?status=active`);
    },
    [scheduleMassNotificationAndArchive, history, site, saveContent],
  );

  const onSchedule: SubmitHandler<NotifyNotificationsFormData> = useCallback(
    async (data) => {
      setIsScheduleLoading(true);
      const content = await saveContent(data);
      if (!content) {
        setIsScheduleLoading(false);
        return;
      }
      const contentIsPublished = content.status === "Active" || content.status === "Archived";
      await scheduleMassNotificationAndArchive({
        contentId: content.id,
        ...(contentIsPublished
          ? {}
          : {
              sendAt: {
                type: ScheduleType.Schedule,
                date: new Date(data.sendAt!),
              },
            }),
        archiveAt: data.archiveAt
          ? {
              type: ScheduleType.Schedule,
              date: new Date(data.archiveAt),
            }
          : {
              type: ScheduleType.Unschedule,
            },
      });
      setIsScheduleLoading(false);
      history.push(`/${site}/notify/notifications?status=scheduled`);
    },
    [scheduleMassNotificationAndArchive, history, site, saveContent],
  );

  return {
    convertContentToFormData,
    addMediaFile,
    onContinue,
    onSaveDraft,
    isSaveDraftLoading,
    onSend,
    isSendLoading,
    onSchedule,
    isScheduleLoading,
  };
}
